import * as React from 'react';
import { FC, useEffect, useState } from 'react';

import { spacing } from '@rbilabs/universal-components/themes';
import { StyleSheet, View } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import { PullToReloadScrollView, useLoadMoreAtBottom } from 'components/pull-to-reload';
import { useLoyaltyUserTransactionsQuery } from 'generated/graphql-gateway';
import { useLocalizedTransactionTransformer } from 'pages/loyalty/transactions/use-localized-transaction-transformer';
import { useAuthContext } from 'state/auth';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { WEB_SMALL_MAX_WIDTH } from 'utils/constants';

import { LoyaltyBackground } from '../loyalty-background';

import { HistorySignUp } from './history-sign-up/history-sign-up';
import {
  StyledLoyaltyClaimPointsLink,
  StyledLoyaltyNoRewardPoints,
} from './loyalty-history.styled';
import { LoyaltyHistoryView } from './loyalty-history.view';
import { LoyaltyHistorySkeletonLoader } from './loylaty-history-skeleton-loader';
import { PointsExpirationCard } from './points-expiration';
import { Transaction } from './types';

/**
 *
 * Loyalty History view container for RBI Loyalty Program
 * Displays tables with loyalty transaction history
 *
 */
export const LoyaltyHistory: FC = () => {
  const [tableData, setTableData] = useState<Transaction[]>([]);
  const transactionTransformer = useLocalizedTransactionTransformer();
  const { claimPointsEnabled } = useLoyaltyContext();
  const { loyaltyUser, loading: loyaltyLoading } = useLoyaltyUserState({
    refreshOnMountOrFocus: true,
  });
  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const { isAuthenticated } = useAuthContext();
  const [showLoading, setShowLoading] = useState(true);

  const [isForceRefreshing, setForceRefresh] = useState<boolean>(false);

  const {
    data,
    loading: transactionsLoading,
    refetch: refetchTransactionHistory,
  } = useLoyaltyUserTransactionsQuery({
    skip: !loyaltyId,
    variables: { loyaltyId },
  });

  const forceRefresh = async () => {
    // We need this delay in order to avoid cutting the spinning animation
    setTimeout(async () => {
      setForceRefresh(true);
      await refetchTransactionHistory();
      setForceRefresh(false);
    }, 900);
  };

  const transactions = data?.loyaltyUserV2?.transactions;
  const currentPoints = loyaltyUser?.points || 0;
  const expirationDate = loyaltyUser?.pointsExpiryDateKey || '';
  const shouldRenderExpirationCard = Number(currentPoints) > 0 && expirationDate;

  // Showing one row only first for the lazy loading (native only)
  const [visibleTransactions, setVisibleTransactions] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const ROWS_PER_PAGE = 2;

  useEffect(() => {
    if (transactions?.length) {
      // Hides the 0 crowns rows from the list
      const filteredTransactions = transactions.filter(
        ({ pointsEarned, pointsUsed, pointsDeducted, bonusPointsEarned }) =>
          bonusPointsEarned || pointsEarned || pointsUsed || pointsDeducted
      );

      setTableData(filteredTransactions.map(transactionTransformer));
    }
  }, [transactionTransformer, transactions]);

  useEffect(() => {
    if (!loyaltyLoading && !transactionsLoading) {
      setShowLoading(false);
    }
  }, [loyaltyLoading, transactionsLoading]);

  useEffect(() => {
    if (!loyaltyId) {
      return;
    }
    refetchTransactionHistory({ loyaltyId });
  }, [loyaltyId, refetchTransactionHistory]);

  const loadMoreHistoryTransactions = () => {
    if (!loadingMore) {
      setLoadingMore(true);
      setTimeout(() => {
        // Increasing rows quantity (lazy loading)
        setVisibleTransactions(
          prevVisibleHistoryTransactions => prevVisibleHistoryTransactions + ROWS_PER_PAGE
        );
        setLoadingMore(false);
      }, 600);
    }
  };

  const { onScroll } = useLoadMoreAtBottom(loadMoreHistoryTransactions);

  if (showLoading || isForceRefreshing) {
    return <LoyaltyHistorySkeletonLoader />;
  }

  return (
    <PullToReloadScrollView
      isDisabled={!isAuthenticated}
      onRefresh={forceRefresh}
      onScroll={onScroll}
      contentContainerStyle={centeredRowContainerStyles.container}
    >
      <View style={styles.wrapper}>
        {!isAuthenticated ? (
          <HistorySignUp />
        ) : (
          <LoyaltyBackground fullHeight>
            {shouldRenderExpirationCard && <PointsExpirationCard loyaltyUser={loyaltyUser} />}
            {tableData.length ? (
              <>
                {claimPointsEnabled ? <StyledLoyaltyClaimPointsLink renderAsButton /> : null}
                <LoyaltyHistoryView
                  tableData={tableData}
                  visibleTransactions={visibleTransactions}
                  loadingMore={loadingMore}
                />
              </>
            ) : (
              <>
                {shouldRenderExpirationCard && claimPointsEnabled && (
                  <StyledLoyaltyClaimPointsLink renderAsButton />
                )}
                <StyledLoyaltyNoRewardPoints />
                {!shouldRenderExpirationCard && claimPointsEnabled && (
                  <StyledLoyaltyClaimPointsLink renderAsButton />
                )}
              </>
            )}
          </LoyaltyBackground>
        )}
      </View>
    </PullToReloadScrollView>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    ...centeredRowContainerStyles.content,
    padding: spacing.$4,
    maxWidth: WEB_SMALL_MAX_WIDTH,
  },
});
